import type { ReactNode } from 'react'
import { Box, Link, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { BATIKAL_PRIVACY_URL } from '@batical/common'

const pSx = { color: 'text.secondary', lineHeight: 1.7, mb: 1.5 } as const
const h2Sx = { fontWeight: 700, fontSize: '1.05rem', mt: 3, mb: 1.5, color: 'text.primary' } as const
const h3Sx = { fontWeight: 600, fontSize: '0.95rem', mt: 2, mb: 1, color: 'text.primary' } as const

function P({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" sx={pSx}>
      {children}
    </Typography>
  )
}

function Ul({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5, color: 'text.secondary' }}>
      {items.map(item => (
        <Typography key={item} component="li" variant="body2" sx={{ mb: 0.75, lineHeight: 1.65 }}>
          {item}
        </Typography>
      ))}
    </Box>
  )
}

function H2({ children }: { children: ReactNode }) {
  return (
    <Typography component="h2" variant="h6" sx={h2Sx}>
      {children}
    </Typography>
  )
}

function H3({ children }: { children: ReactNode }) {
  return (
    <Typography component="h3" variant="subtitle2" sx={h3Sx}>
      {children}
    </Typography>
  )
}

function Quote({ children }: { children: ReactNode }) {
  return (
    <Box
      component="blockquote"
      sx={{
        m: 0,
        mb: 1.5,
        pl: 2,
        borderLeft: '3px solid',
        borderColor: 'primary.main',
        color: 'text.secondary',
        fontStyle: 'italic'
      }}>
      <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
        {children}
      </Typography>
    </Box>
  )
}

export function CguV21Content() {
  return (
    <>
      <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 2, fontWeight: 500 }}>
        Version 3.0 — Dernière mise à jour : 16 mai 2026
      </Typography>

      <H2>AVERTISSEMENT IMPORTANT</H2>
      <P>
        L&apos;Application BatiKal constitue un outil numérique d&apos;aide au calcul fourni exclusivement à des fins
        pédagogiques, d&apos;apprentissage, d&apos;exercice, de simulation et d&apos;information générale.
      </P>
      <Ul
        items={[
          'Les Résultats sont produits automatiquement et reposent sur des modèles simplifiés.',
          'Ils ne constituent pas des études techniques opposables.',
          'Ils ne remplacent pas un bureau d’études, un ingénieur ou un architecte.',
          'Ils ne doivent jamais être utilisés comme unique base de décision dans un projet réel.'
        ]}
      />
      <P>
        Toute utilisation des Résultats dans un contexte réel doit obligatoirement faire l&apos;objet d&apos;une
        validation préalable par un professionnel qualifié et assuré.
      </P>

      <H2>PRÉAMBULE</H2>
      <P>
        Les présentes Conditions Générales d&apos;Utilisation (les « CGU ») régissent l&apos;accès et l&apos;utilisation
        de l&apos;application BatiKal (l&apos;« Application »), accessible via terminaux mobiles, plateformes iOS /
        Android, interfaces web et supports numériques associés, éditée par BatiKal (l&apos;« Éditeur »).
      </P>
      <Ul
        items={[
          'Service numérique de type SaaS ; calculs automatisés.',
          'Modèles pouvant être incomplets, simplifiés ou approximatifs.',
          'Résultats pouvant différer substantiellement d’un calcul professionnel complet.',
          'Aucune certification réglementaire ni validation normative.',
          'Aucune mission d’ingénierie, de maîtrise d’œuvre, de contrôle technique ou de validation réglementaire.'
        ]}
      />
      <P>
        Politique de confidentialité :{' '}
        <Link href={BATIKAL_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          {BATIKAL_PRIVACY_URL}
        </Link>
      </P>

      <H3>Identité de l&apos;Éditeur (LCEN art. 6-III)</H3>
      <Ul
        items={[
          'Nom / raison sociale : [À COMPLÉTER]',
          'Statut : [À COMPLÉTER — particulier / auto-entrepreneur / société]',
          'Adresse postale : [À COMPLÉTER]',
          'Email de contact : contact@batikal-app.com',
          'Directeur de la publication : [À COMPLÉTER]',
          'Hébergeur : Google Firebase Hosting — Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA — https://firebase.google.com'
        ]}
      />

      <H2>ARTICLE 0 – DISPOSITIONS IMPÉRATIVES ET HIÉRARCHIE</H2>
      <H3>0.1</H3>
      <P>
        Les CGU s&apos;appliquent sous réserve du droit français et européen impératif (Code civil, Code de la
        consommation, Code de la construction et de l&apos;habitation, RGPD, loi Informatique et Libertés, responsabilité
        civile).
      </P>
      <H3>0.2</H3>
      <Ul
        items={[
          'Aucune exclusion de responsabilité en cas de dol ou faute lourde.',
          'Aucune exclusion des droits impératifs des consommateurs.',
          'Aucun écart aux dispositions d’ordre public.'
        ]}
      />
      <H3>0.3</H3>
      <P>En cas de contradiction, la disposition impérative prévaut.</P>
      <H3>0.4</H3>
      <P>
        Si une clause est nulle ou abusive, les autres demeurent valides ; substitution par une stipulation valide
        d&apos;effet équivalent.
      </P>

      <H2>ARTICLE 1 – DÉFINITIONS</H2>
      <H3>1.1 Utilisateur</H3>
      <P>Toute personne physique ou morale accédant à l&apos;Application.</P>
      <H3>1.2 Consommateur</H3>
      <P>Utilisateur personne physique agissant hors cadre professionnel (Code de la consommation).</P>
      <H3>1.3 Résultats</H3>
      <P>Données, valeurs, estimations, exports, graphiques ou contenus générés automatiquement.</P>
      <H3>1.4 Contexte réel</H3>
      <Ul
        items={[
          'Chantier, ouvrage réel, dossier réglementaire, marché, projet de construction.',
          'Décision technique ou réalisation effective.'
        ]}
      />
      <H3>1.5 Professionnel qualifié</H3>
      <P>Bureau d&apos;études, ingénieur, architecte, économiste, contrôleur technique, etc.</P>

      <H2>ARTICLE 2 – ACCEPTATION DES CGU</H2>
      <P>
        Toute utilisation vaut acceptation pleine des CGU et de la Politique de confidentialité. L&apos;Éditeur peut
        conserver la preuve de l&apos;acceptation (version, date, traces techniques). En cas de refus, cesser
        immédiatement l&apos;utilisation.
      </P>

      <H2>ARTICLE 3 – FINALITÉ EXCLUSIVE DE L’APPLICATION</H2>
      <P>Fourniture exclusive à des fins pédagogiques, d&apos;apprentissage, d&apos;exercice, de simulation et d&apos;information générale.</P>
      <P>Les Résultats ne constituent en aucun cas une étude technique, note de calcul réglementaire, document d&apos;exécution, validation normative, avis professionnel ou document contractuel opposable.</P>
      <P>Les modèles peuvent simplifier, omettre des paramètres ou différer des méthodes professionnelles réglementaires.</P>

      <H2>ARTICLE 4 – ABSENCE DE SUBSTITUTION À UN PROFESSIONNEL</H2>
      <P>L&apos;Application ne remplace aucun professionnel qualifié. Aucune relation de maîtrise d&apos;œuvre, sous-traitance, conseil technique, validation réglementaire, contrôle technique ou conception d&apos;ouvrage n&apos;est créée.</P>
      <P>L&apos;Utilisateur demeure seul responsable de ses obligations professionnelles (assurances, signatures, conformité de ses livrables).</P>

      <H2>ARTICLE 5 – INTERDICTION D’USAGE OPÉRATIONNEL</H2>
      <Ul
        items={[
          'Interdiction d’utiliser les Résultats comme unique base de décision, validation réglementaire, note de calcul signée ou justification opposable.',
          'Contexte réel : vérification et validation préalables par un professionnel qualifié et assuré.',
          'Usage contraire : responsabilité exclusive de l’Utilisateur.'
        ]}
      />

      <H2>ARTICLE 6 – ABSENCE D’ASSURANCE ET DE CERTIFICATION</H2>
      <P>
        L&apos;Éditeur ne fournit aucune assurance décennale, dommages-ouvrage, garantie constructeur ni garantie
        technique sur les ouvrages. L&apos;Application n&apos;est certifiée, homologuée ni validée par un organisme de
        contrôle technique.
      </P>

      <H2>ARTICLE 7 – ÉVOLUTION DES ALGORITHMES</H2>
      <P>
        L&apos;Éditeur peut modifier algorithmes, méthodes, hypothèses et bibliothèques à tout moment. Aucune stabilité
        ni reproductibilité des Résultats n&apos;est garantie. Aucune obligation de veille réglementaire exhaustive ni
        de mise à jour immédiate.
      </P>

      <H2>ARTICLE 8 – DISPONIBILITÉ ET CYBERSÉCURITÉ</H2>
      <P>Service fourni « en l&apos;état » et « selon disponibilité ». Aucune garantie d&apos;absence d&apos;erreur, d&apos;interruption, de bug, de cyberattaque ni de conservation permanente des données. Sauvegarde à la charge de l&apos;Utilisateur.</P>
      <H3>8.4 – Abonnements et Consommateurs</H3>
      <P>
        Si l&apos;Utilisateur est Consommateur et souscrit un service payant, les dispositions impératives du Code de la
        consommation s&apos;appliquent. Les limitations des articles 10 et 11 s&apos;appliquent dans les limites
        autorisées ; les clauses manifestement abusives pourront être écartées par le juge.
      </P>

      <H2>ARTICLE 9 – RESPONSABILITÉ DE L’UTILISATEUR</H2>
      <P>
        L&apos;Utilisateur assume les risques d&apos;interprétation et d&apos;exploitation des Résultats. Il est seul
        responsable des données saisies, des décisions, des ouvrages et des conséquences envers les tiers. Il ne présente
        pas les Résultats comme validés par l&apos;Éditeur.
      </P>

      <H2>ARTICLE 10 – RESPONSABILITÉ DE L’ÉDITEUR</H2>
      <H3>10.1 – Principe</H3>
      <P>Responsabilité civile pour dommages directement et personnellement imputables, dans les limites ci-après.</P>
      <H3>10.2 – Exclusions (sous réserve de 10.4)</H3>
      <Ul
        items={[
          '(a) Dommages indirects (pertes d’exploitation, CA, données hors faute lourde/dol, image, etc.).',
          '(b) Usage en Contexte réel sans validation professionnelle (art. 5.2).',
          '(c) Erreurs liées aux données saisies ou à leur interprétation.',
          '(d) Non-conformités réglementaires d’un ouvrage ou document professionnel.',
          '(e) Force majeure (art. 13).'
        ]}
      />
      <P>
        Ces exclusions ne visent pas à exclure la responsabilité pour faute directement imputable à l&apos;Éditeur (droit
        commun), sous réserve de l&apos;article 10.4.
      </P>
      <H3>10.3 – Plafonnement</H3>
      <P>
        Sous réserve de 10.4, dommages directs contractuels plafonnés au plus faible entre : sommes versées sur 12 mois
        précédant le fait générateur ; ou 100 € si aucune somme versée. Ce plafond ne s&apos;applique pas aux
        Consommateurs lorsque les dispositions impératives du Code de la consommation s&apos;y opposent ; dans ce cas,
        la responsabilité de l&apos;Éditeur s&apos;exerce dans les limites du droit commun.
      </P>
      <H3>10.4 – Ordre public</H3>
      <Ul
        items={[
          'Dol ou faute lourde de l’Éditeur.',
          'Dommage corporel causé par faute directement imputable à l’Éditeur.',
          'Garanties légales des Consommateurs.',
          'Loi interdisant toute limitation.'
        ]}
      />
      <H3>10.5 – Absence de garantie de résultat</H3>
      <P>Aucune garantie d&apos;exactitude, exhaustivité ou adéquation normative, sauf garanties légales impératives.</P>
      <H3>10.6 – Délai de réclamation</H3>
      <P>
        Pour les Utilisateurs professionnels, réclamation écrite sous un (1) an à compter de la connaissance du
        dommage. Pour les Consommateurs, les délais de prescription légaux (notamment cinq ans, art. 2224 C. civ.)
        s&apos;appliquent et ne sont pas raccourcis par les présentes.
      </P>

      <H2>ARTICLE 11 – INDEMNISATION</H2>
      <P>
        L&apos;Utilisateur garantit et indemnise l&apos;Éditeur (frais d&apos;avocat inclus) pour violation des CGU,
        usage non conforme, usage en Contexte réel sans validation, ou présentation trompeuse des Résultats — sous
        réserve que l&apos;Utilisateur soit responsable et que les fautes exclusivement imputables à l&apos;Éditeur
        (10.4) ne soient pas couvertes.
      </P>

      <H2>ARTICLE 12 – DONNÉES PERSONNELLES – RGPD</H2>
      <P>
        Voir la{' '}
        <Link href={BATIKAL_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          Politique de confidentialité
        </Link>
        . Droits : accès, rectification, effacement, opposition, limitation, portabilité, réclamation CNIL.
      </P>

      <H2>ARTICLE 13 – FORCE MAJEURE</H2>
      <P>
        Pas de responsabilité pour inexécution due à un événement échappant au contrôle raisonnable (panne réseau,
        catastrophe, cyberattaque massive sous réserve de négligence grave, décision administrative, guerre,
        indisponibilité prestataire), sous réserve des dispositions impératives.
      </P>

      <H2>ARTICLE 14 – PROPRIÉTÉ INTELLECTUELLE</H2>
      <P>
        Contenus, algorithmes, interfaces, marques et bases protégés. Reproduction ou ingénierie inverse interdite,
        sous réserve des exceptions légales impératives (notamment décompilation à des fins d&apos;interopérabilité,
        CPI art. L122-6-1). Licence technique limitée à l&apos;hébergement et l&apos;exécution des calculs.
      </P>

      <H2>ARTICLE 15 – CLAUSES GÉNÉRALES</H2>
      <Ul
        items={[
          'Divisibilité (cf. art. 0.4).',
          'Survie des clauses responsabilité, indemnisation, limitations, obligations utilisateur, nature informative.',
          'Non-renonciation par tolérance.',
          'Preuve : journaux, horodatages CGU, traces d’utilisation.',
          'Version française prévalente.',
          'Intégralité de l’accord avec la Politique de confidentialité.'
        ]}
      />

      <H2>ARTICLE 16 – DROIT APPLICABLE ET LITIGES</H2>
      <P>Droit français applicable, sans préjudice des règles impératives de protection des consommateurs.</P>
      <P>
        Consommateurs : tout litige doit faire l&apos;objet d&apos;une réclamation préalable adressée à l&apos;Éditeur
        à <Link href="mailto:contact@batikal-app.com">contact@batikal-app.com</Link>. En cas d&apos;échec, le
        Consommateur peut recourir gratuitement à un médiateur de la consommation (art. L612-1 C. conso.).
        L&apos;Éditeur n&apos;a pas désigné de médiateur tant que l&apos;Application est fournie à titre gratuit
        sans contrepartie financière ; en cas de souscription payante, les coordonnées du médiateur compétent
        seront communiquées dans les CGU et dans l&apos;interface de paiement avant toute commande. Le Consommateur
        peut également utiliser la plateforme européenne de règlement en ligne des litiges :{' '}
        <Link href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
          ec.europa.eu/consumers/odr
        </Link>
        .
      </P>
      <P>Professionnels : tribunaux du ressort du siège social de l&apos;Éditeur, sauf règle impérative contraire.</P>

      <H2>ARTICLE 17 – APPLE / GOOGLE</H2>
      <P>
        Les présentes CGU sont conclues exclusivement entre l&apos;Utilisateur et l&apos;Éditeur ; Apple Inc. et
        Google LLC n&apos;y sont pas parties et ne sont pas responsables de l&apos;Application ni de son contenu,
        sous réserve de leurs propres conditions de distribution.
      </P>
      <P>
        Apple Inc. et ses filiales sont toutefois reconnues comme tiers bénéficiaires des présentes CGU ; à ce
        titre, Apple Inc. est en droit (et est réputée avoir accepté ce droit) de faire valoir les présentes CGU
        à l&apos;encontre de l&apos;Utilisateur en tant que tiers bénéficiaire, conformément aux exigences de
        l&apos;App Store.
      </P>
      <P>
        L&apos;Utilisateur déclare ne pas se trouver dans un pays soumis à un embargo du gouvernement des États-Unis
        ou désigné comme « pays soutenant le terrorisme », et ne pas figurer sur une liste de parties interdites
        ou restreintes du gouvernement des États-Unis.
      </P>

      <H2>ARTICLE 18 – MENTIONS IN-APP RECOMMANDÉES</H2>
      <Quote>
        « Outil d&apos;exercice et de simulation – Résultats non opposables. Validation obligatoire par un professionnel
        qualifié avant toute utilisation réelle. »
      </Quote>
      <Quote>« BatiKal ne réalise aucune mission d&apos;ingénierie ni validation réglementaire. »</Quote>
      <Typography variant="body2" sx={{ ...pSx, mt: 3 }}>
        <Link component={RouterLink} to="/login" underline="hover">
          Retour à la connexion
        </Link>
      </Typography>
    </>
  )
}
